import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useMatch } from 'react-router-dom';
import { isMobileViewport } from '@utils/viewport';
import { readNavCollapsed, writeNavCollapsed } from '@utils/sidebarPreference';
import { APP_BUILDER_ROUTE } from '../pages/Apps/builder/userAppStatus';
import { SidebarContext } from './useSidebar';

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMobile, setIsMobile] = useState(isMobileViewport);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isEditorRailExpanded, setIsEditorRailExpanded] = useState(false);
  const [isProductSwitcherOpen, setIsProductSwitcherOpen] = useState(false);
  const [savedNavCollapsed, setSavedNavCollapsed] = useState(readNavCollapsed);
  // The builder always opens on the rail; an expand there is not saved and ends with the page.
  const [builderExpandedAt, setBuilderExpandedAt] = useState<string | null>(null);
  const { pathname } = useLocation();
  const onBuilder = useMatch(APP_BUILDER_ROUTE) !== null;
  if (!onBuilder && builderExpandedAt !== null) setBuilderExpandedAt(null);

  const builderCollapsed = builderExpandedAt !== pathname;
  const isNavCollapsed = !isMobile && (onBuilder ? builderCollapsed : savedNavCollapsed);

  const setNavCollapsed = useCallback(
    (collapsed: boolean) => {
      if (onBuilder) {
        setBuilderExpandedAt(collapsed ? null : pathname);
        return;
      }
      setSavedNavCollapsed(collapsed);
      writeNavCollapsed(collapsed);
    },
    [onBuilder, pathname],
  );

  useEffect(() => {
    const onResize = () => {
      const mobile = isMobileViewport();
      setIsMobile(mobile);
      if (!mobile) setIsMobileOpen(false);
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const openMobile = useCallback(() => setIsMobileOpen(true), []);
  const closeMobile = useCallback(() => setIsMobileOpen(false), []);

  const setEditorRailExpanded = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      setIsEditorRailExpanded((prev) =>
        typeof next === 'function' ? next(prev) : next,
      );
    },
    [],
  );

  const openProductSwitcher = useCallback(() => setIsProductSwitcherOpen(true), []);
  const closeProductSwitcher = useCallback(
    () => setIsProductSwitcherOpen(false),
    [],
  );
  const toggleProductSwitcher = useCallback(
    () => setIsProductSwitcherOpen((open) => !open),
    [],
  );

  const value = useMemo(
    () => ({
      isMobile,
      isMobileOpen,
      openMobile,
      closeMobile,
      isEditorRailExpanded,
      setEditorRailExpanded,
      isNavCollapsed,
      setNavCollapsed,
      isProductSwitcherOpen,
      openProductSwitcher,
      closeProductSwitcher,
      toggleProductSwitcher,
    }),
    [
      isMobile,
      isMobileOpen,
      openMobile,
      closeMobile,
      isEditorRailExpanded,
      setEditorRailExpanded,
      isNavCollapsed,
      setNavCollapsed,
      isProductSwitcherOpen,
      openProductSwitcher,
      closeProductSwitcher,
      toggleProductSwitcher,
    ],
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
};
